/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { ComponentChildren, ComponentProps } from 'preact';
import { BaseButton } from '../UNSAFE_BaseButton';
import { Text } from '../UNSAFE_Text';
import { useComponentTheme } from '../hooks/UNSAFE_useComponentTheme';
import { ButtonLayoutStyles, ButtonLayoutVariantOptions } from './themes/ButtonLayoutStyles.css';
import { ButtonLayoutRedwoodTheme } from './themes/redwood/ButtonLayoutTheme';

export type ButtonDisplay = 'icons' | 'label' | 'all';
export type LayoutSizes =
  | ComponentProps<typeof BaseButton>['size']
  | 'embeddedxs'
  | 'embeddedsm'
  | 'embeddedmd'
  | 'embeddedlg';

export type Props = {
  /**
   *The default slot is the button's text label. The oj-c-button element accepts plain text or DOM nodes as children for the default slot."
   */
  children?: ComponentChildren;

  /**
   *The suffix is appended to the text label, used for menu buttons.
   */
  suffix?: string;

  /**
   * The startIcon is the button's start icon.
   */
  startIcon?: ComponentChildren;

  /**
   * The endIcon is the button's end icon.
   */
  endIcon?: ComponentChildren;

  /**
   * Styling can be used for customizing icon layout by limiting padding ("embedded")
   */
  styling?: 'default' | 'embedded';

  /**
   * The size indicates how large the button is rendered.
   */
  size?: ComponentProps<typeof BaseButton>['size'];

  /**
   * Display indicates whether the label, the icons, or all elements should be rendered by the button.
   */
  display?: ButtonDisplay;
};

const isNullOrUndefined = (
  value: ComponentChildren | null | undefined
): value is null | undefined => {
  return value === null || value === undefined;
};

export function ButtonLayout({
  size = 'md',
  display = 'all',
  styling = 'default',
  ...props
}: Props) {
  const isShowIcons = display === 'all' || display === 'icons';
  const isShowText = display === 'all' || display === 'label';
  const isOnlyIcon = props.startIcon && (display === 'icons' || !props.children) && !props.endIcon;
  const hasTextStartIcon = !isNullOrUndefined(props.startIcon) && display === 'all';
  const hasTextEndIcon = !isNullOrUndefined(props.endIcon) && isShowIcons;
  const isTwoIcons = !isNullOrUndefined(props.startIcon) && !isNullOrUndefined(props.endIcon);
  const isTwoIconsOnly = isTwoIcons && display === 'icons';

  const { classes: startIconClasses } = useComponentTheme<
    ButtonLayoutVariantOptions,
    ButtonLayoutStyles
  >(ButtonLayoutRedwoodTheme, {
    target: 'startIcon',
    size: styling == 'default' ? size : (('embedded' + size) as LayoutSizes),
    onlyIcon: isOnlyIcon ? 'isOnlyIcon' : 'notOnlyIcon',
    twoIconsOnly: isTwoIconsOnly ? 'isTwoIconsOnly' : 'notTwoIconsOnly'
  });

  const { classes: labelClasses, styles: labelStyles } = useComponentTheme<
    ButtonLayoutVariantOptions,
    ButtonLayoutStyles
  >(ButtonLayoutRedwoodTheme, {
    target: 'label',
    size: styling == 'default' ? size : (('embedded' + size) as LayoutSizes),
    textEndIcon: hasTextEndIcon ? 'hasTextEndIcon' : 'notTextEndIcon',
    textStartIcon: hasTextStartIcon ? 'hasTextStartIcon' : 'notTextStartIcon',
    suffix: props.suffix ? 'hasSuffix' : 'notSuffix',
    twoIcons: isTwoIcons ? 'hasTwoIcons' : 'notTwoIcons'
  });

  const { classes: suffixClasses } = useComponentTheme<
    ButtonLayoutVariantOptions,
    ButtonLayoutStyles
  >(ButtonLayoutRedwoodTheme, {
    target: 'suffix',
    size: styling == 'default' ? size : (('embedded' + size) as LayoutSizes),
    textStartIcon: hasTextStartIcon ? 'hasTextStartIcon' : 'notTextStartIcon',
    twoIcons: isTwoIcons ? 'hasTwoIcons' : 'notTwoIcons'
  });

  const { classes: endIconClasses } = useComponentTheme<
    ButtonLayoutVariantOptions,
    ButtonLayoutStyles
  >(ButtonLayoutRedwoodTheme, {
    target: 'endIcon',
    textEndIcon: hasTextEndIcon ? 'hasTextEndIcon' : 'notTextEndIcon',
    twoIcons: isTwoIcons ? 'hasTwoIcons' : 'notTwoIcons',
    size: styling == 'default' ? size : (('embedded' + size) as LayoutSizes)
  });

  return (
    <span class={labelStyles.container}>
      {isShowIcons && props.startIcon && <span class={startIconClasses}>{props.startIcon}</span>}
      {isShowText && props.children && <span class={labelClasses}> {props.children} </span>}
      {isShowText && props.suffix && (
        <span class={suffixClasses}>
          <Text truncation={'ellipsis'} weight={'inherit'}>
            {props.suffix}
          </Text>
        </span>
      )}
      {isShowIcons && props.endIcon && <span class={endIconClasses}>{props.endIcon}</span>}
    </span>
  );
}

ButtonLayout.displayName = 'ButtonLayout';
